import GridViewIcon from "@mui/icons-material/GridView"
import SearchIcon from "@mui/icons-material/Search"
import TocIcon from "@mui/icons-material/Toc"
import UploadIcon from "@mui/icons-material/Upload"
import {
	Box,
	Button,
	Divider,
	Grid,
	IconButton,
	InputBase,
	Paper,
	SortDirection,
	Tab,
	Tabs,
	Tooltip,
} from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { format } from "date-fns"
import {
	ChangeEvent,
	FormEvent,
	FunctionComponent,
	SyntheticEvent,
	useCallback,
	useEffect,
	useState,
} from "react"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css" // theme css file
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import FileList from "../../components/file/list"
import SpinnerCard from "../../components/file/spinner/card"
import EnhancedTable from "../../components/file/table"
import FiltersFile from "../../components/filters/file"
import Sort from "../../components/sort"
import { AppDispatch } from "../../store"
import { BankEssential } from "../../store/actions/bank"
import {
	CampaignsState,
	CorporateEssential,
} from "../../store/actions/corporates"
import { fetchFiles, getDownload, getFiles } from "../../store/actions/file"
import { selectCorporate } from "../../store/corporates/corporates-all-slice"
import { resetDefault } from "../../store/files-list/files-slice"
import { RootState } from "../../store/reducers"

export interface IDistinteProperties {}

export interface IDatePicker {
	startDate: undefined | Date
	endDate: undefined | Date
	key: string
}

const DistinteList: FunctionComponent<IDistinteProperties> = () => {
	const theme = useTheme()
	const { t } = useTranslation()
	const navigate = useNavigate()
	const dispatch = useDispatch<AppDispatch>()
	const authState = useSelector((state: RootState) => state.auth)
	const bankSelectedState: BankEssential | undefined = useSelector(
		(state: RootState) => state.banksUser.selected
	)
	const corporateIdSelected: string | undefined = useSelector(
		(state: RootState) => state?.corporatesUser?.selected?.id
	)

	const corporateListState: CorporateEssential[] = useSelector(
		(state: RootState) => state.corporatesUser.list
	)

	const idBankForPaydoUser: string | undefined = useSelector(
		(state: RootState) => state.banksUser?.list?.[0]?.id
	)
	const campaignSelectedState: CampaignsState | undefined = useSelector(
		(state: RootState) => state.campaignsUser.selected
	)
	const corporateSelectedState = useSelector(
		(state: RootState) => state.corporatesUser.selected
	)
	const fileLoadingState = useSelector(
		(state: RootState) => state.files.loading
	)
	const fileFetchLoadingState = useSelector(
		(state: RootState) => state.files.loadingFetch
	)
	const filenextUrlState = useSelector(
		(state: RootState) => state.files.list.nextUrl
	)

	const permissions = useSelector(
		(state: RootState) => state.auth?.user?.permissions
	)

	const hasCrudFileEnabled: boolean = permissions
		? permissions.some(
				(perm) => perm.permissionName === "CRUD_FILE" && perm.enabled === true
			)
		: false

	const hasViewFileEnabled: boolean = permissions
		? permissions.some(
				(perm) => perm.permissionName === "VIEW_FILE" && perm.enabled === true
			)
		: false

	// const fileHasNextUrlState = useSelector((state: RootState) => state.files.list.page.);
	const [value, setValue] = useState<0 | 1>(0)
	const [open, setOpen] = useState<boolean>(true)
	const [sortCriteria, setSortCriteria] = useState<
		[{ field: string; order: SortDirection }]
	>([{ field: "id", order: "desc" }])
	const [filterName, setFilterName] = useState<undefined | string>(undefined)
	const [filterBank, setFilterBank] = useState<undefined | string>(
		authState.user?.idBank ?? bankSelectedState?.id
	)

	const [filterCorporate, setFilterCorporate] = useState<undefined | string>(
		corporateIdSelected ?? authState.user?.corporateSenderId
	)
	const [filterCampaign, setFilterCampaign] = useState<undefined | string>(
		undefined
	)
	const [statuses, setStatuses] = useState<string[]>([])
	const [viewType, setViewType] = useState<undefined | string>("grid")
	const [datePicker, setDatePicker] = useState<IDatePicker[]>([
		{
			startDate: null,
			endDate: null,
			key: "selection",
		},
	])

	const { ref, inView } = useInView({
		threshold: 1,
		triggerOnce: false,
	})

	const ButtonViewType = styled(Button)(({ theme }) => ({
		"&:hover": {
			boxShadow:
				"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
			backgroundColor: theme.palette.secondary.contrastText,
			color: theme.palette.secondary.main,
			"& svg": {
				background: `${theme.palette.secondary.contrastText} !important`,
				color: `${theme.palette.secondary.main} !important`,
			},
		},
	}))

	const handleChangeTab = (event: SyntheticEvent, newValue: 0 | 1) => {
		setValue(newValue)
	}

	const handleDownload = (fileId: string, filename: string) => {
		if (bankSelectedState?.id && fileId)
			dispatch(
				getDownload({
					bankId: bankSelectedState?.id,
					fileId: fileId,
					filename: filename,
				})
			)
				.then(() => {})
				.catch(() => {})
		else {
			console.log("No data selected!") // TODO manage error
		}
	}

	const handleSearch = () => {
		const filters: { [key: string]: any } = setFiltersFunc()
		const userSort = sortCriteria
			.map((criteria) => `${criteria.field}:${criteria.order}`)
			.join(",")
		dispatch(
			getFiles({ page: 0, pageSize: 10, filter: filters, sort: userSort })
		)
			.then(() => {})
			.catch(() => {})
	}

	useEffect(() => {
		resetDefault()
	}, [])

	useEffect(() => {
		!hasViewFileEnabled && navigate("/")
	}, [hasViewFileEnabled])

	useEffect(() => {
		handleSearch()
	}, [authState?.isAuthenticate, open, filterBank, sortCriteria])

	useEffect(() => {
		setFilterBank(authState.user?.idBank ?? idBankForPaydoUser)
	}, [authState.user?.idBank, idBankForPaydoUser])

	useEffect(() => {
		setFilterCorporate(corporateIdSelected || authState.user?.corporateSenderId)
	}, [authState.user?.corporateSenderId, corporateIdSelected])

	useEffect(() => {
		if (filterCorporate) {
			const infoCorporate = corporateListState?.find(
				(corporate: CorporateEssential) => corporate.id === filterCorporate
			)
			dispatch(selectCorporate(infoCorporate))
		}
	}, [filterCorporate])

	const setFiltersFunc = useCallback(() => {
		const filter: { [key: string]: any } = {}

		filter.open = open
		if (filterName) filter.filename = filterName
		if (filterBank) {
			filter.idBank = filterBank
		}
		if (filterCorporate) {
			filter.corporateSenderId = filterCorporate
		}
		if (filterCampaign) {
			filter.campaign = filterCampaign
		}
		if (datePicker?.[0]?.startDate || datePicker?.[0]?.endDate) {
			filter.rangeDate =
				format(datePicker?.[0]?.startDate as number | Date, "yyyy-MM-dd") +
				":" +
				format(datePicker?.[0]?.endDate as number | Date, "yyyy-MM-dd")
		}
		if (statuses.length > 0) {
			filter.status = statuses
		}
		return filter
	}, [
		filterBank,
		filterCampaign,
		campaignSelectedState?.idLogo,
		filterCorporate,
		corporateSelectedState?.corporateSenderId,
		filterName,
		open,
		statuses,
		datePicker?.[0]?.startDate,
		datePicker?.[0]?.endDate,
	])

	useEffect(() => {
		if (inView && filenextUrlState && !fileFetchLoadingState) {
			dispatch(fetchFiles({ url: filenextUrlState }))
				.then(() => {})
				.catch(() => {})
		}
	}, [
		authState?.isAuthenticate,
		inView,
		filenextUrlState,
		fileFetchLoadingState,
	])

	return (
		<>
			<Box sx={{ margin: "-16px", backgroundColor: "#FFF" }}>
				<Tabs
					variant="fullWidth"
					indicatorColor="secondary"
					textColor="inherit"
					value={value}
					onChange={handleChangeTab}
					aria-label="Tabs type"
				>
					<Tab
						onClick={() => setOpen(true)}
						label={t("tabs.open", { ns: "distinte" })}
					/>
					<Tab
						onClick={() => setOpen(false)}
						label={t("tabs.closed", { ns: "distinte" })}
					/>
				</Tabs>
			</Box>

			<Grid
				container
				justifyContent={"flex-end"}
				sx={{ marginTop: "32px", marginBottom: "16px" }}
			>
				{hasCrudFileEnabled && (
					<Button
						onClick={() => navigate("/upload")}
						sx={{ borderRadius: 0, textTransform: "initial" }}
						variant="contained"
						id="userSignInSubmit"
						type="button"
					>
						<UploadIcon
							aria-label={t("accessibility.uploadIcon", { ns: "distinte" })}
						/>{" "}
						{t("uploadBtn", { ns: "distinte" })}
					</Button>
				)}
			</Grid>

			<Paper
				component="form"
				onSubmit={(event: FormEvent) => {
					event.stopPropagation()
					event.preventDefault()
					handleSearch()
				}}
				sx={{
					p: "2px 4px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<InputBase
					value={filterName}
					onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
						setFilterName(event.target.value)
					}
					sx={{ ml: 1, flex: 1 }}
					placeholder={t("search.placeholder", { ns: "distinte" })}
					inputProps={{
						"aria-label": t("search.placeholder", { ns: "distinte" }),
					}}
				/>
				<IconButton
					type="button"
					sx={{ p: "10px" }}
					aria-label={t("search.accessibility.label", { ns: "distinte" })}
					onClick={() => handleSearch()}
				>
					<SearchIcon />
				</IconButton>
				<Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
				<Grid container gap="16px" sx={{ width: "fit-content" }}>
					<FiltersFile
						filterBank={filterBank}
						setFilterBank={setFilterBank}
						filterCampaign={filterCampaign}
						setFilterCampaign={setFilterCampaign}
						filterCorporate={filterCorporate}
						setFilterCorporate={setFilterCorporate}
						handleSearch={handleSearch}
						datePicker={datePicker}
						setDatePicker={setDatePicker}
						statuses={statuses}
						setStatuses={setStatuses}
					/>
					<Sort
						handleSearch={handleSearch}
						sortCriteria={sortCriteria}
						setSortCriteria={setSortCriteria}
						sortingOptions={[
							"id",
							"campaignName",
							"corporateSenderId",
							"creationDate",
							"idBank",
							"originalFileName",
							"plicksCount",
							"status",
							"totalAmount",
						]}
						availableOptionsInitial={[
							"campaignName",
							"corporateSenderId",
							"creationDate",
							"idBank",
							"originalFileName",
							"plicksCount",
							"status",
							"totalAmount",
						]}
					/>
				</Grid>
			</Paper>

			<Grid
				container
				alignItems={"center"}
				justifyContent={"flex-end"}
				width={"100%"}
				sx={{ margin: "16px 0" }}
			>
				<Tooltip title={t("view.accessibility.grid", { ns: "distinte" })}>
					<ButtonViewType
						variant={viewType === "grid" ? "contained" : "outlined"}
						aria-label={t("view.accessibility.grid", { ns: "distinte" })}
						onClick={() => setViewType("grid")}
						sx={{
							backgroundColor:
								viewType === "grid"
									? theme.palette.secondary.main
									: theme.palette.primary.main,
						}}
						startIcon={
							<GridViewIcon
								style={{
									backgroundColor:
										viewType === "grid"
											? theme.palette.secondary.main
											: theme.palette.primary.main,
									color:
										viewType === "grid"
											? theme.palette.secondary.contrastText
											: theme.palette.primary.contrastText,
								}}
							/>
						}
					></ButtonViewType>
				</Tooltip>
				<Tooltip title={t("view.accessibility.table", { ns: "distinte" })}>
					<ButtonViewType
						// variant={viewType === 'table' ? 'contained' : 'outlined'}
						aria-label={t("view.accessibility.table", { ns: "distinte" })}
						onClick={(e) => setViewType("table")}
						sx={{
							marginLeft: "16px",
							backgroundColor:
								viewType === "table"
									? theme.palette.secondary.main
									: theme.palette.primary.main,
						}}
						startIcon={
							<TocIcon
								style={{
									backgroundColor:
										viewType === "table"
											? theme.palette.secondary.main
											: theme.palette.primary.main,
									color:
										viewType === "table"
											? theme.palette.secondary.contrastText
											: theme.palette.primary.contrastText,
								}}
							/>
						}
					></ButtonViewType>
				</Tooltip>
			</Grid>
			{fileLoadingState ? (
				<SpinnerCard />
			) : (
				<>
					{viewType === "grid" ? (
						<FileList />
					) : (
						<EnhancedTable
							handleDownload={handleDownload}
							sortCriteria={sortCriteria}
							setSortCriteria={setSortCriteria}
						/>
					)}
					{fileFetchLoadingState ? (
						<SpinnerCard />
					) : (
						<div ref={ref} style={{ height: "30px" }}></div>
					)}
				</>
			)}
		</>
	)
}

export default DistinteList
